import React, { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  AccordionIconPosition,
  AccordionRotation,
  Accordions,
  Gutter,
  Layout,
  Divider,
} from '@axiom/ui';

import type { ChatNotice } from '../../types/chat.types';

import { useChatHistoryData } from './useChatHistoryData';
import { ChatHistoryEntries } from './ChatHistoryEntries';
import { ChatHistorySection } from './ChatHistorySection';

export type ChatHistoryProps = {
  activeChatId: string | null;
  setActiveChatId: ({
    chatId,
    isFull,
  }: {
    chatId: string | null;
    isFull?: boolean;
  }) => void;
  /** The list's writes report their own failures, and the notice belongs to `Spark`. */
  setNotice: Dispatch<SetStateAction<ChatNotice | null>>;
};

export const ChatHistory = ({
  activeChatId,
  setActiveChatId,
  setNotice,
}: ChatHistoryProps) => {
  const { chatHistory, hideChat, renameChat, unhideChat } = useChatHistoryData({
    setNotice,
  });
  /*
    Hidden conversations are dropped here because nothing else drops them: the list query
    carries no `deleted` predicate, so the endpoint hands hidden rows over with the rest.
    `deleted` is nullish on the wire and a row arriving without it is shown - a conversation
    wrongly shown can be hidden again, where one wrongly hidden looks like data loss.
  */
  const hiddenLogs = chatHistory.filter(chat => chat.deleted);
  const visibleLogs = chatHistory.filter(chat => !chat.deleted);

  const [renamingChatId, setRenamingChatId] = useState<string | null>(null);

  return (
    <Layout direction="vertical" name="CHAT_HISTORY" stretched>
      <Gutter bottom="16px" horizontal="8px">
        <Accordions
          animated={AccordionRotation.HALF}
          icon="arrow-down"
          iconPosition={AccordionIconPosition.BESIDE}
          minimal
          name="CHAT_HISTORY_SECTIONS"
        >
          {visibleLogs.length > 0 ? (
            <ChatHistorySection defaultOpen label="Chats" name="SHOW_CHATS">
              <ChatHistoryEntries
                activeChatId={activeChatId}
                chats={visibleLogs}
                hideChat={hideChat}
                renameChat={renameChat}
                renamingChatId={renamingChatId}
                setActiveChatId={setActiveChatId}
                setRenamingChatId={setRenamingChatId}
                unhideChat={unhideChat}
              />
            </ChatHistorySection>
          ) : null}
          {visibleLogs.length > 0 && hiddenLogs.length > 0 ? (
            <Divider name="CHAT_SECTIONS_SEPARATOR" />
          ) : null}

          {/*
            Below the visible list rather than merged into it in date order, which would reorder the
            list under the reader's cursor on every hide.
          */}
          {hiddenLogs.length > 0 ? (
            <ChatHistorySection label="Hidden Chats" name="SHOW_HIDDEN_CHATS">
              <ChatHistoryEntries
                activeChatId={activeChatId}
                chats={hiddenLogs}
                hideChat={hideChat}
                renameChat={renameChat}
                renamingChatId={renamingChatId}
                setActiveChatId={setActiveChatId}
                setRenamingChatId={setRenamingChatId}
                unhideChat={unhideChat}
              />
            </ChatHistorySection>
          ) : null}
        </Accordions>
      </Gutter>
    </Layout>
  );
};
